import React, { useEffect, useState, useMemo } from "react";
import Modal from "./Modal";
import api from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import PackagesTab from "./PackagesTab";
import BookingRow, { STATUS_BADGES } from "./dashboard/BookingRow";
import { UsersPanel, PromoPanel } from "./dashboard/AdminPanels";
import AdminCalendarOverview from "./dashboard/AdminCalendarOverview";
import PricingPanel from "./dashboard/PricingPanel";
import RevenueChart from "./dashboard/RevenueChart";
import MyPromosTab from "./dashboard/MyPromosTab";
import AccountSettingsTab from "./dashboard/AccountSettingsTab";
import SiteTextEditorPanel from "./dashboard/SiteTextEditorPanel";
import SocialLinksPanel from "./dashboard/SocialLinksPanel";
import AnalyticsPanel from "./dashboard/AnalyticsPanel";
import DbResetPanel from "./dashboard/DbResetPanel";
import GalleryManagerPanel from "./dashboard/GalleryManagerPanel";
import AdminCreateBookingModal from "./dashboard/AdminCreateBookingModal";
import {
  Calendar, Users, BarChart3, Tag, Check, Loader2, RefreshCw, CreditCard,
  Sparkles, BellRing, DollarSign, Package, Settings, Type, Share2, TrendingUp, Trash2, Images, CalendarPlus,
} from "lucide-react";

const TABS_BY_ROLE = {
  customer: [
    { id: "bookings", label: "Mé rezervace", icon: Calendar },
    { id: "packages", label: "Výhodné balíčky", icon: Package },
    { id: "promos", label: "Moje slevy", icon: Tag },
    { id: "settings", label: "Nastavení účtu", icon: Settings },
  ],
  employee: [
    { id: "stats", label: "Přehled", icon: BarChart3 },
    { id: "bookings", label: "Rezervace", icon: Calendar },
    { id: "calendar", label: "Kalendář", icon: Calendar },
    { id: "analytics", label: "Návštěvy", icon: TrendingUp },
  ],
  admin: [
    { id: "stats", label: "Přehled", icon: BarChart3 },
    { id: "bookings", label: "Rezervace", icon: Calendar },
    { id: "calendar", label: "Kalendář", icon: Calendar },
    { id: "users", label: "Uživatelé", icon: Users },
    { id: "promo", label: "Promo kódy", icon: Tag },
    { id: "pricing", label: "Ceník", icon: DollarSign },
    { id: "site-text", label: "Texty webu", icon: Type },
    { id: "gallery", label: "Galerie", icon: Images },
    { id: "social", label: "Sociální sítě", icon: Share2 },
    { id: "analytics", label: "Návštěvy", icon: TrendingUp },
    { id: "db-reset", label: "Reset DB", icon: Trash2 },
  ],
};

export default function Dashboard({ open, onClose, initialTab }) {
  const { user } = useAuth();
  const tabs = user ? TABS_BY_ROLE[user.role] || TABS_BY_ROLE.customer : [];
  const [tab, setTab] = useState(tabs[0]?.id);
  // Open → jump to initialTab if it's valid for this user's role; otherwise
  // fall back to the first tab. eslint deps are intentionally narrow so we
  // don't fight the `tabs` array identity on every render.
  useEffect(() => {
    if (!open) return;
    const wanted = initialTab && tabs.some((t) => t.id === initialTab) ? initialTab : tabs[0]?.id;
    if (wanted) setTab(wanted);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, user?.role, initialTab]);

  if (!user) return null;

  return (
    <Modal open={open} onClose={onClose} title={`${user.role === "admin" ? "Admin panel" : user.role === "employee" ? "Zaměstnanecký panel" : "Můj účet"} · ${user.firstName}`} size="full" testId="dashboard-modal">
      <div className="grid md:grid-cols-[220px_1fr] gap-6">
        <div className="space-y-1.5">
          {tabs.map(t => (
            <button key={t.id} onClick={() => setTab(t.id)} className={`w-full text-left rounded-xl px-4 py-3 text-sm flex items-center gap-2 transition ${
              tab === t.id ? "bg-apex-gold text-apex-bg font-semibold" : "text-apex-cream/80 hover:bg-apex-card"
            }`} data-testid={`dashboard-tab-${t.id}`}>
              <t.icon className="w-4 h-4" /> {t.label}
            </button>
          ))}
        </div>
        <div className="md:h-[72vh] md:overflow-y-auto md:pr-1 min-h-[400px]">
          {tab === "stats" && <StatsPanel />}
          {tab === "bookings" && (user.role === "customer" ? <MyBookings /> : <AllBookings role={user.role} />)}
          {tab === "packages" && user.role === "customer" && <PackagesTab />}
          {tab === "promos" && user.role === "customer" && <MyPromosTab />}
          {tab === "settings" && user.role === "customer" && <AccountSettingsTab />}
          {tab === "calendar" && <AdminCalendarOverview />}
          {tab === "users" && user.role === "admin" && <UsersPanel />}
          {tab === "promo" && user.role === "admin" && <PromoPanel />}
          {tab === "pricing" && user.role === "admin" && <PricingPanel />}
          {tab === "site-text" && user.role === "admin" && <SiteTextEditorPanel />}
          {tab === "gallery" && user.role === "admin" && <GalleryManagerPanel />}
          {tab === "social" && user.role === "admin" && <SocialLinksPanel />}
          {tab === "analytics" && (user.role === "admin" || user.role === "employee") && <AnalyticsPanel />}
          {tab === "db-reset" && user.role === "admin" && <DbResetPanel />}
        </div>
      </div>
    </Modal>
  );
}

function StatCard({ icon: Icon, label, value, accent }) {
  return (
    <div className="rounded-2xl bg-apex-card border border-apex-line p-5">
      <div className={`w-10 h-10 rounded-lg ${accent || "bg-apex-gold/15 text-apex-gold"} flex items-center justify-center`}>
        <Icon className="w-5 h-5" />
      </div>
      <div className="mt-3 text-2xl font-display text-apex-cream font-bold">{value}</div>
      <div className="text-xs text-apex-cream/60 uppercase tracking-wider mt-1">{label}</div>
    </div>
  );
}

function StatsPanel() {
  const [stats, setStats] = useState(null);
  useEffect(() => {
    api.get("/employee/stats").then(r => setStats(r.data)).catch(() => {});
  }, []);
  if (!stats) return <Loader2 className="w-6 h-6 animate-spin text-apex-gold" />;
  return (
    <div className="space-y-6">
      <h3 className="font-display text-2xl text-apex-cream font-bold">Přehled</h3>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        <StatCard icon={Calendar} label="Celkem rezervací" value={stats.total_bookings} />
        <StatCard icon={Calendar} label="Čeká na potvrzení" value={stats.pending_bookings} accent="bg-amber-500/15 text-amber-300" />
        <StatCard icon={Check} label="Potvrzeno" value={stats.confirmed_bookings} accent="bg-sky-500/15 text-sky-300" />
        <StatCard icon={CreditCard} label="Zaplaceno" value={stats.paid_bookings} accent="bg-emerald-500/15 text-emerald-300" />
        <StatCard icon={Sparkles} label="Dokončeno" value={stats.completed_bookings} accent="bg-purple-500/15 text-purple-300" />
        <StatCard icon={BarChart3} label="Tržby celkem" value={`${Math.round(stats.total_revenue).toLocaleString("cs-CZ")} Kč`} />
      </div>
      <RevenueChart />
    </div>
  );
}

function MyBookings() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const load = () => {
    setLoading(true);
    api.get("/bookings/my-bookings").then(r => setItems(r.data || [])).finally(() => setLoading(false));
  };
  useEffect(load, []);
  if (loading) return <Loader2 className="w-6 h-6 animate-spin text-apex-gold" />;
  if (!items.length) return <div className="text-apex-cream/70">Zatím nemáte žádné rezervace.</div>;
  return (
    <div className="space-y-3">
      {items.map(b => <BookingRow key={b.id || b._id} b={b} mine onChange={load} />)}
    </div>
  );
}

function AllBookings({ role }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [sortBy, setSortBy] = useState("newest"); // smart | newest | oldest | service-soonest | name
  const [searchTerm, setSearchTerm] = useState("");
  const [scanBusy, setScanBusy] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const load = () => {
    setLoading(true);
    const q = filter === "all" ? "" : `?status=${filter}`;
    api.get(`/employee/bookings${q}`).then(r => setItems(r.data?.bookings || [])).finally(() => setLoading(false));
  };
  useEffect(load, [filter]);

  const displayed = useMemo(() => {
    let list = [...items];
    // Search filter (customer name / email / phone)
    if (searchTerm.trim()) {
      const q = searchTerm.toLowerCase().trim();
      list = list.filter((b) => {
        const c = b.customer || {};
        const name = `${c.firstName || ""} ${c.lastName || ""}`.toLowerCase();
        return (
          name.includes(q) ||
          (c.email || "").toLowerCase().includes(q) ||
          (c.phone || "").toLowerCase().includes(q) ||
          (b.serviceType || "").toLowerCase().includes(q)
        );
      });
    }
    // Sort
    const ts = (b) => new Date(b.createdAt || 0).getTime();
    const svc = (b) => `${b.date || ""}T${b.time || "00:00"}`;
    if (sortBy === "newest") {
      list.sort((a, b) => ts(b) - ts(a));
    } else if (sortBy === "oldest") {
      list.sort((a, b) => ts(a) - ts(b));
    } else if (sortBy === "service-soonest") {
      list.sort((a, b) => svc(a).localeCompare(svc(b)));
    } else if (sortBy === "name") {
      list.sort((a, b) => {
        const an = `${a.customer?.firstName || ""} ${a.customer?.lastName || ""}`.toLowerCase();
        const bn = `${b.customer?.firstName || ""} ${b.customer?.lastName || ""}`.toLowerCase();
        return an.localeCompare(bn, "cs");
      });
    }
    // "smart" — keep backend order (pending oldest first, others by service date)
    return list;
  }, [items, sortBy, searchTerm]);

  const triggerAutoScan = async () => {
    if (!confirm("Spustit nyní sken auto-připomínek?\n\nProjde všechny potvrzené nezaplacené rezervace s termínem do 24h a pošle zákazníkovi e-mailovou připomínku.")) return;
    setScanBusy(true);
    try {
      const { data } = await api.post("/employee/auto-reminder/scan");
      alert(data?.message || "Sken dokončen.");
      load();
    } catch (e) {
      alert(e?.response?.data?.detail || "Sken se nezdařil.");
    } finally {
      setScanBusy(false);
    }
  };
  return (
    <div>
      <div className="flex flex-wrap items-center gap-2 mb-3">
        {["all", "pending", "confirmed", "paid", "completed", "cancelled"].map(f => (
          <button key={f} onClick={() => setFilter(f)} className={`px-3 py-1.5 text-xs rounded-lg border ${
            filter === f ? "bg-apex-gold text-apex-bg border-apex-gold" : "bg-apex-card text-apex-cream/75 border-apex-line"
          }`} data-testid={`booking-filter-${f}`}>{f === "all" ? "Vše" : STATUS_BADGES[f]?.label || f}</button>
        ))}
        {role === "admin" && (
          <button
            onClick={() => setShowCreateModal(true)}
            className="text-xs px-3 py-1.5 rounded-lg bg-apex-gold text-apex-bg font-semibold hover:brightness-110 inline-flex items-center gap-1.5"
            data-testid="admin-create-booking-btn"
          >
            <CalendarPlus className="w-3.5 h-3.5" /> Nová rezervace
          </button>
        )}
        {role === "admin" && (
          <button
            onClick={triggerAutoScan}
            disabled={scanBusy}
            className="text-xs px-3 py-1.5 rounded-lg border border-apex-gold/40 text-apex-gold hover:bg-apex-gold/10 disabled:opacity-50 inline-flex items-center gap-1.5"
            title="Spustí kontrolu auto-připomínek (jinak běží automaticky každých 30 min)"
            data-testid="auto-reminder-scan-btn"
          >
            <BellRing className="w-3.5 h-3.5" />
            {scanBusy ? "Skenuje…" : "Spustit auto-připomínky"}
          </button>
        )}
        <button onClick={load} className="ml-auto text-xs text-apex-gold flex items-center gap-1"><RefreshCw className="w-3 h-3" /> Obnovit</button>
      </div>
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <input
          type="text"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Hledat (jméno, e-mail, telefon, služba)"
          className="flex-1 min-w-[200px] px-3 py-1.5 rounded-lg bg-apex-bg border border-apex-line text-apex-cream text-xs focus:outline-none focus:border-apex-gold/60"
          data-testid="booking-search-input"
        />
        <select
          value={sortBy}
          onChange={(e) => setSortBy(e.target.value)}
          className="px-3 py-1.5 rounded-lg bg-apex-bg border border-apex-line text-apex-cream text-xs focus:outline-none focus:border-apex-gold/60"
          data-testid="booking-sort-select"
        >
          <option value="smart">Inteligentní (čekající první)</option>
          <option value="newest">Nejnovější rezervace</option>
          <option value="oldest">Nejstarší rezervace</option>
          <option value="service-soonest">Nejbližší termín služby</option>
          <option value="name">Abecedně (jméno)</option>
        </select>
        <span className="text-xs text-apex-cream/55">{displayed.length} / {items.length}</span>
      </div>
      {loading ? <Loader2 className="w-6 h-6 animate-spin text-apex-gold" /> : (
        <div className="space-y-3">
          {displayed.length === 0 && <div className="text-apex-cream/65">Žádné rezervace.</div>}
          {displayed.map(b => <BookingRow key={b.id || b._id} b={b} role={role} onChange={load} />)}
        </div>
      )}
      {role === "admin" && (
        <AdminCreateBookingModal open={showCreateModal} onClose={() => setShowCreateModal(false)} onCreated={load} />
      )}
    </div>
  );
}
